import React, { useEffect } from "react";
import { Alert, AlertTitle, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import usePushNotifications from "../../../hooks/usePushNotifications";
import { getUserFromLocalStorage } from "../../../utils/utils";
import { resolvePushDestination } from "../../../utils/pushDestinations";
import { markNotificationsAsRead } from "../../../services/notificationState";

const NID_PARAM = "notificationId";

/**
 * Registers web push after login, keeps the unread badge fresh, shows foreground
 * messages as a toast, and marks the notification (or announcement) read when a push is tapped.
 */
const PushNotificationsManager: React.FC = () => {
  const navigate = useNavigate();
  let hasToken = false;
  try {
    hasToken = !!getUserFromLocalStorage()?.userToken;
  } catch {
    hasToken = false;
  }
  const { message, clear } = usePushNotifications(hasToken);

  // Push tapped while the app was closed: the service worker opens /path?notificationId=<id>.
  useEffect(() => {
    if (!hasToken) return;
    try {
      const url = new URL(window.location.href);
      const nid = url.searchParams.get(NID_PARAM);
      if (!nid) return;
      url.searchParams.delete(NID_PARAM);
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
      void markNotificationsAsRead([nid]);
    } catch {
      /* ignore */
    }
  }, [hasToken]);

  // Push tapped while a tab was already open: the service worker posts a message.
  useEffect(() => {
    if (!hasToken || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const d = e?.data;
      if (d?.type === "PUSH_NAVIGATE" && typeof d.notificationId === "string") {
        void markNotificationsAsRead([d.notificationId]);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [hasToken]);

  if (!message) return null;

  return (
    <Snackbar
      open
      autoHideDuration={8000}
      onClose={clear}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={clear}
        sx={{ cursor: "pointer", backgroundColor: "#4A7AF2", color: "#fff" }}
        onClick={() => {
          // Transactional pushes carry the inbox row id; announcement pushes carry the campaign id.
          const readId = message.notificationId || (message.campaignId ? `campaign:${message.campaignId}` : null);
          if (readId) void markNotificationsAsRead([readId]);
          navigate(resolvePushDestination(message.destination));
          clear();
        }}
      >
        <AlertTitle>{message.title}</AlertTitle>
        {message.body}
      </Alert>
    </Snackbar>
  );
};

export default PushNotificationsManager;
